import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { SettingsLayout } from './ProgramSettings/SettingsLayout';

/**
 * Program Designer — the program build/design surface. Under the orgLevelAdmin
 * restructure, Continue lands here for Course / Guided Course / Hybrid.
 *
 * The design panel is ALWAYS `position: fixed` (so it stays in the viewport
 * stacking context and never falls behind the header mid-animation). framer's
 * `layout` morphs it between the measured docked rect (collapsed, rounded
 * border) and the full viewport (expanded, no frame, covers header/nav).
 * Toggle via the button left of "Save Changes" (icon flips expand ↔ contract).
 */
function ProgramDesigner() {
  const [expanded, setExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement | null>(null);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const [dock, setDock] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  // Capture where the panel sits when docked. Skip while expanded so a resize
  // mid-expansion doesn't overwrite the collapsed rect with the full-screen one.
  const measure = useCallback(() => {
    if (expandedRef.current) return;
    const el = slotRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setDock({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && slotRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(slotRef.current);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [measure]);

  // Re-capture the docked rect when returning to collapsed (handles a resize
  // that happened while expanded).
  useLayoutEffect(() => {
    if (!expanded) measure();
  }, [expanded, measure]);

  const target =
    expanded || !dock
      ? { top: 0, left: 0, width: '100vw', height: '100vh' }
      : { top: dock.top, left: dock.left, width: dock.width, height: dock.height };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#0F0F0F',
        p: expanded ? 0 : 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* In-flow slot reserves the docked space; the fixed panel overlays it. */}
      <Box ref={slotRef} sx={{ flex: 1, minHeight: 0 }} />
      <motion.div
        layout
        transition={{ type: 'spring', stiffness: 300, damping: 32 }}
        style={{
          position: 'fixed',
          top: target.top,
          left: target.left,
          width: target.width,
          height: target.height,
          // Below the nav drawer + editor dialogs (MUI Modal = 1300) so the menu
          // opens ON TOP, but above the app header/appBar (~1100) so expanded
          // still covers it.
          zIndex: 1200,
          background: '#1C1C1C',
          border: expanded ? 'none' : '1px solid #2A2A2A',
          borderRadius: expanded ? 0 : 8,
          overflow: 'auto'
        }}
      >
        <SettingsLayout
          view="design"
          refetchKey={0}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((e) => !e)}
        />
      </motion.div>
    </Box>
  );
}

export default ProgramDesigner;
